import { useStore } from '../store/useStore';

const LOOK: Record<string, { dot: string; label: string; tone: string }> = {
  idle: { dot: 'bg-done', label: 'Synced', tone: 'text-ink-soft' },
  syncing: { dot: 'bg-progress animate-pulse-dot', label: 'Syncing', tone: 'text-ink-soft' },
  offline: { dot: 'bg-blocked', label: 'Offline', tone: 'text-blocked' },
  error: { dot: 'bg-danger', label: 'Sync problem', tone: 'text-danger' },
};

function relativeTime(timestamp: number): string {
  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

/**
 * The heartbeat readout. When offline it states plainly that changes are held
 * locally, because the risk with an offline-first tool is a user assuming their
 * work reached the server when it has not.
 */
export function SyncIndicator() {
  const syncState = useStore((s) => s.syncState);
  const pendingCount = useStore((s) => s.pendingCount);
  const lastSyncedAt = useStore((s) => s.lastSyncedAt);
  const syncError = useStore((s) => s.syncError);
  const refresh = useStore((s) => s.refresh);

  const look = LOOK[syncState] ?? LOOK['idle']!;
  const offline = syncState === 'offline';

  const detail = offline
    ? pendingCount > 0
      ? `${pendingCount} change${pendingCount === 1 ? '' : 's'} waiting to upload`
      : 'Changes are saved on this device'
    : syncError
      ? syncError
      : lastSyncedAt
        ? `Last synced ${relativeTime(lastSyncedAt)}`
        : 'Not synced yet';

  return (
    <button
      type="button"
      onClick={() => void refresh()}
      title={detail}
      className="group flex items-center gap-2.5 rounded-lg border border-line px-2.5 py-1.5 text-left transition-colors hover:bg-hover"
    >
      <span className={`size-2 shrink-0 rounded-full ${look.dot}`} />
      <span className="flex min-w-0 flex-col leading-tight">
        <span className={`text-xs font-medium ${look.tone}`}>
          {look.label}
          {pendingCount > 0 && !offline ? ` · ${pendingCount}` : ''}
        </span>
        <span className="truncate text-[11px] text-ink-faint">{detail}</span>
      </span>
    </button>
  );
}
